import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { PermissionDefinition } from '../../core/models/user.models';
import { PermissionsService } from '../../core/services/permissions.service';
import {
  SaveSubscriptionPlan,
  SubscriptionPlan,
  SubscriptionPlansService,
} from '../../core/services/subscription-plans.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';

@Component({
  selector: 'app-subscription-plans',
  imports: [FormsModule],
  templateUrl: './subscription-plans.html',
  styleUrl: './subscription-plans.scss',
})
export class SubscriptionPlans implements OnInit {
  private readonly plansService = inject(SubscriptionPlansService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly currentUser = inject(CurrentUserService);
  readonly plans = signal<SubscriptionPlan[]>([]);
  readonly permissions = signal<PermissionDefinition[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly success = signal('');
  editingId = '';
  form: SaveSubscriptionPlan = this.empty();
  can(permission: string): boolean { return this.currentUser.hasPermission(permission); }

  ngOnInit(): void {
    this.load();
  }
  load(): void {
    this.loading.set(true);
    this.error.set('');
    forkJoin({ plans: this.plansService.list(true), permissions: this.permissionsService.list() })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (data) => {
          this.plans.set(data.plans);
          this.permissions.set(data.permissions);
        },
        error: (error) =>
          this.error.set(httpErrorMessage(error, 'Unable to load subscription plans.')),
      });
  }
  edit(plan: SubscriptionPlan): void {
    this.editingId = plan.id;
    this.form = { ...plan, permissionIds: [...plan.permissionIds] };
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  togglePermission(id: string, enabled: boolean): void {
    this.form.permissionIds = enabled
      ? [...new Set([...this.form.permissionIds, id])]
      : this.form.permissionIds.filter((value) => value !== id);
  }
  save(): void {
    if (!this.form.name.trim()) {
      this.error.set('Plan name is required.');
      return;
    }
    this.saving.set(true);
    this.error.set('');
    this.success.set('');
    this.plansService
      .save(this.form, this.editingId || undefined)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.success.set(this.editingId ? 'Plan updated.' : 'Plan created.');
          this.cancel();
          this.load();
        },
        error: (error) =>
          this.error.set(httpErrorMessage(error, 'Unable to save subscription plan.')),
      });
  }
  cancel(): void {
    this.editingId = '';
    this.form = this.empty();
  }
  private empty(): SaveSubscriptionPlan {
    return {
      name: '',
      description: '',
      price: 0,
      billingPeriod: 'Monthly',
      isFree: false,
      isDefault: false,
      isActive: true,
      maxUsers: 2,
      maxRoles: 1,
      maxLocations: 1,
      maxProductListings: 25,
      maxNewProductListings: 20,
      maxUsedProductListings: 5,
      trialDays: 0,
      allowCustomRoles: false,
      permissionIds: [],
    };
  }
}
